// Libs
import classNames from 'classnames/bind';
import { Avatar, Box, Button, Grid2, List, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { ConfirmModal, Loading, UnderDevelopment } from '~/components/common';
import { FolderItem, FormFolder } from '~/components/module/folder';
import { DocumentItem, FormDocument } from '~/components/module/document';
// Others
import { IFile, IFolderLocal, IFormDocument } from '~/utils/interface/document';
import { useAppDispatch } from '~/utils/redux/hooks';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { BusinessTypeEnum, RoleEnum } from '~/utils/enum/common';
import { TDocumentType } from '~/utils/type/common';
import { IFolder, IFormFolder } from '~/utils/interface/folder';
import { createFormData, generateUniqueId } from '~/utils/helper/common';
import { uploadMultipleFileAndGetLink } from '~/thunks/file/fileThunk';
// Styles, images, icons
import styles from './DocumentLocal.module.scss';
import { icons } from '~/assets';

type Props = {
  type?: TDocumentType;
  id?: string;
  folders?: IFolderLocal[];
  onChange?: (data: IFolderLocal[]) => void;
  businessType?: BusinessTypeEnum;
};

const cx = classNames.bind(styles);

const DocumentLocal = (props: Props) => {
  //#region Destructuring Props
  const { id, onChange, folders, businessType = BusinessTypeEnum.JOB } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/document']);
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowFormFile, setIsShowFormFile] = useState<boolean>(false);
  const [isShowFormFolder, setIsShowFormFolder] = useState<boolean>(false);

  const [folderList, setFolderList] = useState<IFolderLocal[]>([]);
  const [folderSelected, setFolderSelected] = useState<IFolderLocal>();

  const [documentSelected, setDocumentSelected] = useState<IFile>();

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowConfirmDeleteDocument, setIsShowConfirmDeleteDocument] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const folderItemSelected = useMemo(() => {
    if (!folderSelected) return undefined;

    return {
      id: folderSelected?.id || EMPTY_STRING,
      name: folderSelected?.name || EMPTY_STRING,
      businessId: folderSelected?.businessId || EMPTY_STRING,
      businessType: folderSelected?.businessType || EMPTY_STRING,
      isCurrentApply: folderSelected?.isCurrentApply || false,
      files: folderSelected?.files || [],
    };
  }, [folderSelected]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!folderList || folderList.length === DEFAULT_NUMBER_ZERO) return;

    onChange && onChange(folderList);
  }, [folderList, onChange]);

  useEffect(() => {
    if (!folders || folders.length === DEFAULT_NUMBER_ZERO) return;

    const newFolder = folders.map((folder) => {
      return {
        id: generateUniqueId(),
        ...folder,
        files: folder.files
          ? folder.files?.map((file) => {
              return {
                id: generateUniqueId(),
                ...file,
              };
            })
          : [],
      };
    });

    setFolderList(newFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only initializes from prop on mount; folderList is managed internally thereafter
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowAddFile = () => {
    setDocumentSelected(undefined);
    setIsShowFormFile(true);
  };

  const handleShowAddFolder = () => {
    setFolderSelected(undefined);
    setIsShowFormFolder(true);
  };

  const handleSelectFolder = (folder: IFolderLocal) => {
    if (!folder?.id) return;

    setFolderSelected(folder);
  };

  const handleSaveFolder = (folder: IFormFolder) => {
    if (folder.id) {
      setFolderList((prev) => prev.map((item) => (item.id === folder.id ? { ...item, ...folder } : item)));
    } else {
      const newFolder: IFolderLocal = {
        id: generateUniqueId(),
        ...folder,
      };
      setFolderList((prev) => [...prev, newFolder]);
    }
    setIsShowFormFolder(false);
    setFolderSelected(undefined);
  };

  const handleEditFolder = (folder: IFolderLocal) => {
    setFolderSelected(folder);
    setIsShowFormFolder(true);
  };

  const handleDeleteFolder = (folder: IFolderLocal) => {
    if (!folder) return;

    setFolderSelected(folder);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDeleteFolder = () => {
    if (!folderSelected?.id) return;

    const newTask: IFolderLocal[] = folderList.filter((folder) => folder.id !== folderSelected?.id);
    setFolderList(newTask);
    setFolderSelected(undefined);
    setIsShowConfirmDelete(false);
  };

  const handleCloseFormFile = () => {
    setDocumentSelected(undefined);
    setIsShowFormFile(false);
  };

  const handleCloseFormFolder = () => {
    setFolderSelected(undefined);
    setIsShowFormFolder(false);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setFolderSelected(undefined);
  };

  const handleCloseConfirmDeleteDocument = () => {
    setIsShowConfirmDeleteDocument(false);
    setDocumentSelected(undefined);
  };

  const handleConfirmDeleteDocument = () => {
    if (!documentSelected?.id || !folderSelected?.id) return;

    const updatedFiles = folderSelected?.files?.filter((file) => file.id !== documentSelected.id);

    const updatedFolder: IFolderLocal = {
      ...folderSelected,
      files: updatedFiles,
    };

    setFolderList((prev) => prev.map((folder) => (folder.id === updatedFolder.id ? updatedFolder : folder)));

    setFolderSelected(updatedFolder);

    setDocumentSelected(undefined);
    setIsShowConfirmDeleteDocument(false);
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleSaveDocument = async (data: IFormDocument) => {
    const files = data?.files?.map((file) => ({
      ...file,
      name: file.name,
      file: new File([file.file], file.file.name, {
        type: file.file?.type,
      }),
    }));

    const payload = {
      files: files,
    };

    const formData = await createFormData(payload, 'files');

    setIsLoading(true);

    dispatch(uploadMultipleFileAndGetLink(formData))
      .unwrap()
      .then((res) => {
        if (!res || !res?.data) return;

        const findFolder = folderList?.find((item) => item?.id === data?.folder);

        const updatedFiles = [
          ...(findFolder?.files || []),
          ...res?.data?.map((file) => ({
            id: generateUniqueId(),
            ...file,
          })),
        ];

        const updatedFolder: IFolderLocal = {
          id: findFolder?.id,
          name: findFolder?.name || EMPTY_STRING,
          isCurrentApply: findFolder?.isCurrentApply || false,
          files: updatedFiles,
        };

        setFolderList((prev) => prev.map((item) => (item.id === updatedFolder.id ? updatedFolder : item)));

        setFolderSelected(updatedFolder);

        setIsShowFormFile(false);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteDocument = (file: IFile) => {
    setDocumentSelected(file);

    setIsShowConfirmDeleteDocument(true);
  };
  //#endregion Handle Function

  return (
    <Grid2
      component={'div'}
      id='documentLocalComponent'
      container
      rowSpacing={'30px'}
      columnSpacing={'30px'}
      className={cx('container')}
    >
      <Grid2 size={4}>
        <Stack className={cx('group')} rowGap={'24px'} columnGap={'24px'}>
          <Box component={'div'} className={cx('fileUpload')}>
            <Button className={cx('buttonUpload')} onClick={handleShowAddFile}>
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                gap={'10px'}
                flexWrap={'wrap'}
              >
                <Avatar src={icons.commonIconFileUpload} alt={t('common_alt_img', { ns: 'common/base' })} />

                <Typography component={'p'} className={cx('placeholder')}>
                  {t('component_file_upload_text_btn', { ns: 'common/base' })}
                  <Typography component={'span'} className={cx('placeholderBrowse')}>
                    {t('component_file_upload_browse_btn', { ns: 'common/base' })}
                  </Typography>
                </Typography>
              </Stack>
            </Button>
          </Box>

          <Stack component={'div'} className={cx('folderGroup')} rowGap={'20px'} columnGap={'20px'}>
            <Stack
              component={'div'}
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap={'20px'}
              flexWrap={'wrap'}
              className={cx('folderTitleGroup')}
            >
              <Typography className={cx('title')}>{t('tab_documents_title_folder')}</Typography>

              <Button variant='contained' onClick={handleShowAddFolder}>
                {t('tab_documents_add_folder')}
              </Button>
            </Stack>

            <List component={'div'} id='listFolder' className={cx('listFolder')}>
              {folderList?.map((folder: IFolderLocal, index: number) => {
                const folderItem: IFolder = {
                  id: folder.id || EMPTY_STRING,
                  name: folder.name || EMPTY_STRING,
                  businessId: folder.businessId || EMPTY_STRING,
                  businessType: folder.businessType || EMPTY_STRING,
                  isCurrentApply: folder.isCurrentApply || false,
                  files: folder.files || [],
                };

                return (
                  <FolderItem
                    key={index}
                    folder={folderItem}
                    index={index}
                    selected={folderSelected?.id === folder.id}
                    showAction={
                      folder.isCurrentApply || folder.businessId === id || currentRole !== RoleEnum.TECHNICIAN
                    }
                    onSelect={handleSelectFolder}
                    onEdit={handleEditFolder}
                    onDelete={handleDeleteFolder}
                  />
                );
              })}
            </List>
          </Stack>
        </Stack>
      </Grid2>

      <Grid2 size={'grow'}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          gap={'20px'}
          flexWrap={'wrap'}
          className={cx('documentTitleGroup')}
        >
          <Typography className={cx('title')}>{t('tab_documents_title')}</Typography>

          <List className={cx('listDocument')}>
            {folderItemSelected?.files?.map((file, index) => {
              const fileItem = {
                id: file.id || EMPTY_STRING,
                fieldName: file.fieldName || EMPTY_STRING,
                mimetype: file.mimetype || EMPTY_STRING,
                name: file.name || EMPTY_STRING,
                url: file.url || EMPTY_STRING,
              };
              return (
                <DocumentItem type='local' key={index} document={fileItem} onDelete={handleDeleteDocument} />
              );
            })}
          </List>
        </Stack>
      </Grid2>

      {isShowFormFile && (
        <FormDocument
          businessType={businessType}
          onClose={handleCloseFormFile}
          folders={folderList as IFolder[]}
          onSave={handleSaveDocument}
        />
      )}

      {isShowFormFolder && (
        <FormFolder
          businessType={businessType}
          onClose={handleCloseFormFolder}
          folder={folderItemSelected}
          onSave={handleSaveFolder}
        />
      )}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDeleteFolder}
        />
      )}

      {isShowConfirmDeleteDocument && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDeleteDocument}
          onConfirm={handleConfirmDeleteDocument}
        />
      )}

      {isLoading && <Loading />}
      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Grid2>
  );
};

export default DocumentLocal;
